import {
  BrowserClient,
  defaultStackParser,
  defaultIntegrations,
  makeFetchTransport,
  Hub,
} from '@sentry/browser';
import { Debug, ExtraErrorData } from '@sentry/integrations';
import { Integration } from '@sentry/types';
import { isDev } from '../../helpers/isDev';
import { getCookie } from '../getCookie';
import { S4MPHttpClient } from './integrations/S4MPHttpClient';
import { S4MPEventFilter } from './integrations/S4MPEventFilter';
import { S4MPCaptureConsole } from './integrations/S4MPCaptureConsole';
import { S4MPReportingObserver } from './integrations/S4MPReportingObserver';
import { debugMessage } from './debugMessage';

let Sentry: Hub;

export const sentryInt = () => {
  const debug = getCookie('sentryDebug') === 'true';
  const shouldDebug = debug || isDev;
  const hub = new Hub();
  const integrations: Integration[] = [
    ...defaultIntegrations.filter(integration => integration.name !== 'Dedupe'),
    new S4MPHttpClient(hub, {
      failedRequestStatusCodes: [
        [500, 599],
        [401, 403],
      ],
    }),
    new ExtraErrorData(),
    new S4MPCaptureConsole(hub, { levels: ['error', 'warn', 'debug'] }),
    new S4MPReportingObserver(hub, {
      types: ['crash', 'deprecation', 'intervention'],
    }),
    new S4MPEventFilter(),
  ];

  if (shouldDebug) {
    integrations.push(new Debug());
  }

  // filter out non-s4mp-chrome apps based on basepath of source file
  const client = new BrowserClient({
    dsn: 'https://f3885ee4099a418899864cd005982776@o22381.ingest.sentry.io/4504645579374592',
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    integrations,
    debug: isDev,
    enabled: process.env.NODE_ENV === 'production' || debug,
    release: process.env.NEXT_PUBLIC_SENTRY_VERSION || 'unknown',
  });

  debugMessage('sentry: setting up s4mp client and hub');
  hub.bindClient(client);

  // in theory this should not be necessary...
  window.addEventListener('error', ev => {
    hub.run(currentHub => {
      debugMessage('sentry: capturing s4mp window error');
      currentHub.captureException(ev);
    });
  });

  window.addEventListener('unhandledrejection', ev => {
    hub.run(currentHub => {
      debugMessage('sentry: capturing s4mp unhandledrejection error');
      currentHub.captureException(ev.reason);
    });
  });
  return hub;
};

export const getSentry = () => {
  if (!Sentry) {
    Sentry = sentryInt();
  }
  return Sentry;
};
